import { FC, createElement, SVGProps, useMemo } from "react";

import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import map from "lodash/map";
import { FormattedMessage, injectIntl, IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import { IListingRules } from "~/types/local-types";
import { loadDynamicIcon } from "~/utils/loadDynamicIcon";

import { ReadMoreText } from "../../../utils/ReadMoreToggle";
import {
  productionQuestions,
  hasItem,
  parkingQuestions,
  rulesFAQ,
} from "../data/production-faq-display-sections";

import { analyticEventOnClick } from "./faq-utils";

interface ParkingData {
  is_available: boolean;
  types: string[];
  description: string;
}

interface FormattedMessageShape {
  id: string;
  defaultMessage: string;
}

interface ProductionQuestions {
  can_move_furniture: FormattedMessageShape;
  can_use_smoke_machines: FormattedMessageShape;
  furniture_move_description: FormattedMessageShape;
  has_surveillance: FormattedMessageShape;
  insurance_requirement_description: FormattedMessageShape;
  min_age: FormattedMessageShape;
  nudity_allowed: FormattedMessageShape;
  requires_production_insurance: FormattedMessageShape;
  rules: FormattedMessageShape;
  surveillance_description: FormattedMessageShape;
}

interface ProductionFAQProps {
  listingRules: IListingRules;
  parkingData: ParkingData;
  intl: IntlShape;
  userID: string;
  listingID: string;
  urlPrefix: string;
}

interface FAQSectionProps {
  label: string;
  icon: string;
  faqFn: (answer: string[], intl: IntlShape) => object | ProductionQuestions;
  faqData: IListingRules | ParkingData;
  intl: IntlShape;
  userID: string;
  listingID: string;
  urlPrefix: string;
}

export const FAQSections: FC<FAQSectionProps> = ({
  label,
  icon,
  faqFn,
  faqData,
  intl,
  userID,
  listingID,
  urlPrefix,
}) => {
  const faqKeys = Object.keys(faqFn(undefined as never, intl));
  const IconComponent = useMemo(
    () => (icon ? loadDynamicIcon(icon, 24) : null),
    [icon],
  );

  const isSurveillanceKey = (key: string) =>
    key === "has_surveillance" || key === "surveillance_description";
  return (
    <Accordion
      type="single"
      collapsible
      onValueChange={() => {
        analyticEventOnClick(userID, listingID, label);
      }}
    >
      <AccordionItem value="production-faq-section">
        <AccordionTrigger className="tw-py-24">
          <div className="tw-flex tw-items-center tw-gap-12">
            {IconComponent &&
              createElement(IconComponent, {
                role: "img",
                "aria-label": `faq-icon-${icon}`,
              } as SVGProps<SVGSVGElement>)}
            <Text element="h3" typeface="headline-1-m">
              {label}
            </Text>
          </div>
        </AccordionTrigger>
        <AccordionContent className="tw-ml-36 tw-pb-24 tw-space-y-8">
          {map(faqKeys, (key) => {
            const answer = get(faqData, key);
            const faqHash =
              key === "surveillance_description"
                ? rulesFAQ(answer, urlPrefix)
                : faqFn(answer, intl);
            const returnedLabel = get(faqHash, [key, "label"], "");
            const label =
              "object" === typeof returnedLabel
                ? intl.formatMessage(returnedLabel, { answer: answer })
                : returnedLabel;
            const description = get(faqHash, [key, "description"], "");
            // zeros are acceptable answers
            if (
              !label &&
              (!description || Object.keys(description).length === 0)
            ) {
              return null;
            }

            const formattedRules =
              typeof description === "string"
                ? description.split("\n").map((line: string, index: number) => (
                    <Text className="tw-mb-4" key={index} element="p">
                      {line}
                    </Text>
                  ))
                : null;
            if (isUndefined(answer) || isNull(answer) || "" === answer) {
              return null;
            }

            return (
              <div key={key}>
                {label && <Text typeface="body-2-m">{label}</Text>}
                {(formattedRules || description) && (
                  <ReadMoreText
                    lineClamp={!isSurveillanceKey(key) ? "tw-line-clamp-3" : ""}
                    text={
                      <Text className="tw-text-gray-600" element="p">
                        {formattedRules || description}
                      </Text>
                    }
                    openText={
                      <FormattedMessage
                        id="listing.production_faq.read_more"
                        defaultMessage="Read more"
                      />
                    }
                    closeText={
                      <FormattedMessage
                        id="listing.production_faq.hide"
                        defaultMessage="Hide"
                      />
                    }
                  />
                )}
              </div>
            );
          })}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

const ProductionFAQ: FC<ProductionFAQProps> = ({
  listingRules,
  parkingData,
  intl,
  userID,
  listingID,
  urlPrefix,
}) => {
  const parkingFn = get(parkingQuestions, "parking.faq");

  return (
    <div className="ProductionFAQ">
      {!isEmpty(parkingData) && (
        <FAQSections
          icon="parking"
          label={intl.formatMessage(get(parkingQuestions, "parking.label"))}
          faqFn={parkingFn}
          faqData={parkingData}
          intl={intl}
          userID={userID}
          listingID={listingID}
          urlPrefix={urlPrefix}
        />
      )}
      {map(productionQuestions, (val) => {
        return (
          hasItem(
            // @ts-expect-error - typescript not playing nice with dynamically calling functions with different params
            Object.keys(val.faq(undefined as never, intl)),
            listingRules,
          ) && (
            <FAQSections
              key={intl.formatMessage(val.label)}
              icon={val.icon}
              label={intl.formatMessage(val.label)}
              // @ts-expect-error - have too many different types - will refactor eventually
              faqFn={val.faq}
              faqData={listingRules}
              intl={intl}
              userID={userID}
              listingID={listingID}
              urlPrefix={urlPrefix}
            />
          )
        );
      })}
    </div>
  );
};

export default injectIntl(ProductionFAQ);
